import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Train, Check, ChevronRight, ChevronLeft, PhoneCall } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider, useAppModals } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { AREA_GUIDES, CONTACT } from "../mock";

function Content() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openValuation } = useAppModals();
  const area = AREA_GUIDES.find((a) => a.slug === slug);
  const crumbs = useMemo(
    () => [{ label: "Home", to: "/" }, { label: "Areas", to: "/areas" }, { label: area?.name }],
    [area]
  );
  const otherAreas = useMemo(() => AREA_GUIDES.filter((a) => a.slug !== slug), [slug]);

  if (!area) {
    return (
      <div className="App">
        <Header />
        <div className="mx-auto max-w-[900px] px-5 pt-40 pb-24 text-center">
          <h1 className="font-display text-3xl font-600 text-[#16323f]">Area not found</h1>
          <button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#16323f] px-8 py-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#ec008c]">
            Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Seo
        title={`Letting Agents in ${area.name} (${area.postcode}) | New Space`}
        description={`New Space is your local letting and property management agent in ${area.name}, ${area.postcode}. Expert lettings, tailored fees and reliable management for landlords in ${area.name}.`}
        path={`/areas/${area.slug}`}
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28">
        <img src={area.image} onError={(e) => (e.currentTarget.src = area.fallback)} alt={`${area.name} letting agents`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#16323f]/80" />
        <div className="relative z-10 mx-auto max-w-[1000px] px-5 text-center">
          <div className="mb-6 flex justify-center">
            <Breadcrumbs
              variant="light"
              items={crumbs}
            />
          </div>
          <p className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">
            <MapPin size={14} /> {area.name} &middot; {area.postcode}
          </p>
          <h1 className="mt-4 font-display text-4xl font-700 uppercase leading-tight tracking-wide text-white lg:text-6xl">
            {area.tagline}
          </h1>
          <div className="mt-8 flex justify-center">
            <button onClick={openValuation} className="group flex items-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Free valuation in {area.name}
              <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[#faf7f0] py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1100px] gap-12 px-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f]">
              Renting in {area.name}
            </h2>
            <span className="mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#5b5450]">
              {area.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <aside className="lg:col-span-1">
            <div className="rounded-2xl border border-[#e5ddd0] bg-white p-6 shadow-sm">
              <h3 className="font-display text-lg font-600 uppercase tracking-wide text-[#16323f]">Local highlights</h3>
              <ul className="mt-4 space-y-3">
                {area.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-[#5b5450]">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#35a4a4] text-white">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-start gap-3 border-t border-gray-100 pt-5 text-sm text-[#5b5450]">
                <Train size={18} className="mt-0.5 shrink-0 text-[#35a4a4]" />
                <span>{area.transport}</span>
              </div>
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="mt-5 flex items-center gap-2 text-sm font-semibold text-[#16323f] hover:text-[#ec008c]">
                <PhoneCall size={16} className="text-[#35a4a4]" /> {CONTACT.phone}
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Other areas */}
      <section className="bg-[#f3ede3] py-14">
        <div className="mx-auto max-w-[1100px] px-5 text-center">
          <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">Other areas we cover</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {otherAreas.map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="rounded-full border border-[#16323f]/20 bg-white px-6 py-2.5 text-sm font-semibold text-[#16323f] transition-colors hover:border-[#ec008c] hover:text-[#ec008c]">
                {a.name}
              </Link>
            ))}
            <Link to="/#tenants" className="rounded-full bg-[#16323f] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#ec008c]">
              View all properties
            </Link>
            <Link to="/areas" className="rounded-full border border-[#16323f]/20 bg-white px-6 py-2.5 text-sm font-semibold text-[#16323f] transition-colors hover:border-[#ec008c] hover:text-[#ec008c]">
              All areas we cover
            </Link>
            <Link to="/landlords/hmo-licensing-hackney" className="rounded-full border border-[#16323f]/20 bg-white px-6 py-2.5 text-sm font-semibold text-[#16323f] transition-colors hover:border-[#ec008c] hover:text-[#ec008c]">
              HMO &amp; licensing guide
            </Link>
          </div>
          <div className="mt-8">
            <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-[#16323f] hover:text-[#ec008c]">
              <ChevronLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function AreaGuide() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
